import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AppViewDescriptor } from "../../api";
import { Badge, Button, Dialog, Input, cn } from "../../ui";
import { useShellConfig } from "../context";
import { PUBLIC_VIEW, type NavItem } from "../nav";

/**
 * The per-page permission picker for a user account.
 *
 * The users grid showed a count of granted views and offered no way to change
 * it, so the only route to a permission change was the database.
 *
 * Modelled on the app-account endpoint picker, with one difference that
 * matters: codes the running app does not recognise are kept. An account can
 * hold a view this build has never heard of, from an older release or another
 * app against the same database, and a picker that silently dropped what it
 * could not name would quietly revoke access on every save. They are listed
 * under their own heading so an admin can see and remove them deliberately.
 */
export function ViewPickerDialog({
  open,
  onClose,
  views,
  loading,
  initial,
  onSave,
  username,
}: {
  open: boolean;
  onClose: () => void;
  views: AppViewDescriptor[];
  loading: boolean;
  initial: string[];
  onSave: (codes: string[]) => Promise<void> | void;
  username: string;
}) {
  const { t } = useTranslation();
  const { nav } = useShellConfig();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial));

  /**
   * The route each code opens, read off the app's own nav manifest.
   *
   * Shown next to the label so an admin can see what they are granting without
   * decoding a code. Deliberately display-only: the code stays the identity,
   * because a permission has to outlive the URL it happens to guard today, and
   * one code can guard more than one route.
   */
  const routes = useMemo(() => {
    const found = new Map<string, string>();
    const walk = (items: NavItem[]) => {
      for (const item of items) {
        // A page can be reachable under any of several codes, so each one maps
        // to the same route rather than only the first.
        if (item.view && item.to) {
          for (const code of Array.isArray(item.view) ? item.view : [item.view]) {
            if (!found.has(code)) found.set(code, item.to);
          }
        }
        if (item.children) walk(item.children);
      }
    };
    walk(nav);
    return found;
  }, [nav]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  /** Held codes this build cannot name. Preserved, and shown so they are not a secret. */
  const unknown = useMemo(() => {
    const known = new Set(views.map((view) => view.code));
    return initial.filter((code) => !known.has(code)).sort();
  }, [views, initial]);

  /**
   * Where each code sits in the app's own sidebar: the section it first appears
   * under (top-level pages share "General") and its position there. The picker
   * mirrors the sidebar rather than guessing groups from the code's first word,
   * which put Purchasing under "General" and gave "API", "MANUAL" and "PURCHASE"
   * groups of one.
   */
  const placement = useMemo(() => {
    const general = t("user.views.group.general", "General");
    const place = new Map<string, { group: string; order: number }>();
    let order = 0;
    const visit = (items: NavItem[], section: string | null) => {
      for (const item of items) {
        if (item.children) {
          visit(item.children, section ?? t(item.labelKey));
          continue;
        }
        for (const code of Array.isArray(item.view) ? item.view : [item.view]) {
          if (code && code !== PUBLIC_VIEW && !place.has(code)) {
            place.set(code, { group: section ?? general, order: order++ });
          }
        }
      }
    };
    visit(nav, null);
    return place;
  }, [nav, t]);

  const groups = useMemo(() => {
    // Codes that are not a page (a right such as approving purchase orders) go last.
    const other = t("user.views.group.other", "Other permissions");
    const groupOf = (view: AppViewDescriptor) => placement.get(view.code)?.group ?? other;
    const needle = query.trim().toLowerCase();
    const matching = needle
      ? views.filter((view) => `${view.code} ${view.label} ${groupOf(view)}`.toLowerCase().includes(needle))
      : views;

    const byGroup = new Map<string, AppViewDescriptor[]>();
    for (const view of matching) {
      const list = byGroup.get(groupOf(view)) ?? [];
      list.push(view);
      byGroup.set(groupOf(view), list);
    }
    const rank = (view: AppViewDescriptor) => placement.get(view.code)?.order ?? Number.MAX_SAFE_INTEGER;
    for (const list of byGroup.values()) list.sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));

    // Sections in sidebar order (the first page of each decides), "Other permissions" last.
    return [...byGroup.entries()].sort(([a, listA], [b, listB]) => {
      if (a === other) return 1;
      if (b === other) return -1;
      const first = (list: AppViewDescriptor[]) => (list[0] ? rank(list[0]) : Number.MAX_SAFE_INTEGER);
      return first(listA) - first(listB);
    });
  }, [views, query, placement, t]);

  const matchingUnknown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? unknown.filter((code) => code.toLowerCase().includes(needle)) : unknown;
  }, [unknown, query]);

  const toggle = (code: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const setGroup = (group: AppViewDescriptor[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const view of group) {
        if (on) next.add(view.code);
        else next.delete(view.code);
      }
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await onSave([...selected]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("user.views.edit", "Edit page access")}
      description={t("user.views.description", "Which pages {{username}} can open.", { username })}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void save()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("grid.search", "Search")}
              className="pl-8"
            />
          </div>
          <Badge variant="accent" size="md">
            {t("user.views.selected", "{{count}} granted", { count: selected.size })}
          </Badge>
        </div>

        {loading ? (
          <p className="text-[13px] text-ink-faint">{t("common.loading", "Loading...")}</p>
        ) : (
          <div className="flex max-h-[26rem] flex-col gap-4 overflow-y-auto">
            {groups.map(([group, entries]) => {
              const all = entries.every((view) => selected.has(view.code));
              return (
                <section key={group}>
                  <div className="mb-1.5 flex items-center gap-2">
                    <h3 className="text-[12px] font-semibold uppercase tracking-wider text-ink-muted">
                      {group}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setGroup(entries, !all)}
                      className="text-[11px] font-medium text-accent hover:underline"
                    >
                      {all
                        ? t("user.views.clear-group", "Clear")
                        : t("user.views.select-group", "Select all")}
                    </button>
                  </div>

                  <div className="grid gap-1 sm:grid-cols-2">
                    {entries.map((view) => (
                      <label
                        key={view.code}
                        className={cn(
                          "flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-[13px]",
                          selected.has(view.code) ? "bg-accent-soft" : "hover:bg-surface-sunk",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(view.code)}
                          onChange={() => toggle(view.code)}
                          className="mt-0.5 size-3.5 shrink-0 accent-[var(--accent)]"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-ink">{view.label}</span>
                          <span className="block truncate text-[11px] text-ink-faint">
                            {routes.get(view.code) ?? view.code}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </section>
              );
            })}

            {matchingUnknown.length > 0 && (
              <section>
                <h3 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wider text-warn">
                  {t("user.views.unknown", "Leftover permissions")}
                </h3>
                <p className="mb-1.5 text-[11px] text-ink-muted">
                  {t(
                    "user.views.unknown.hint",
                    "These are saved on the account but no longer match any page, so they grant nothing. They are usually left behind when a page is renamed or removed. Untick one to delete it.",
                  )}
                </p>
                <div className="grid gap-1 sm:grid-cols-2">
                  {matchingUnknown.map((code) => (
                    <label
                      key={code}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px]",
                        selected.has(code) ? "bg-warn-soft" : "hover:bg-surface-sunk",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(code)}
                        onChange={() => toggle(code)}
                        className="size-3.5 shrink-0 accent-[var(--accent)]"
                      />
                      <span className="truncate text-ink-muted">{code}</span>
                    </label>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
